/** @file marksOf: the joint marks' input for this frame from the viewport's state (null: no marks). */
import type { EditorState } from '../../state/editorTypes';
import { cssToTarget } from './cssToTarget';
import { jointFracsOf } from './jointFracsOf';
import type { MarksInput } from './jointMarks';
import { leafLengths } from './leafLengths';
import type { PreviewPuppet } from './previewPuppet';
import type { ViewportCtx } from './viewportCtx';

const NONE: readonly string[] = [];
const memo = new WeakMap<readonly string[], { nodes: object; twins: readonly string[] }>();

/** The selection's mirror twins (`node.mirror`), memoised on the selection array (same array while it is unchanged). */
function twinsOf(s: EditorState): readonly string[] {
  const nodes = s.puppet.doc?.puppet.nodes;
  if (nodes === undefined || s.selection.nodes.length === 0) return NONE;
  const hit = memo.get(s.selection.nodes);
  if (hit?.nodes === nodes) return hit.twins;
  const twins = s.selection.nodes.flatMap((n) => { const t = nodes[n]?.mirror; return t === undefined || s.selection.nodes.includes(t) ? [] : [t]; });
  memo.set(s.selection.nodes, { nodes, twins });
  return twins;
}

/** Per preview puppet: its drawable id -> node index map and the leaf-length buffer, made once. */
type LeafScratch = { nodeOf: (drawable: string) => number; buf: Float64Array };
const leafScratch = new WeakMap<PreviewPuppet, LeafScratch>();

/** The scratch of `p` (allocated on its first frame): node index by drawable id from the bundle's puppet. */
function leafScratchOf(p: PreviewPuppet): LeafScratch {
  const hit = leafScratch.get(p);
  if (hit !== undefined) return hit;
  const index = new Map<string, number>();
  for (const d of p.bundle.puppet.drawables) index.set(d.id, p.names.indexOf(d.node));
  const made: LeafScratch = { nodeOf: (id) => index.get(id) ?? -1, buf: new Float64Array(p.names.length) };
  leafScratch.set(p, made);
  return made;
}

/** Joints off (the default): only the selection's bones show, so with no selection there is nothing to mark. */
const nothingToMark = (s: EditorState): boolean => !s.camera.joints && s.selection.nodes.length === 0;

/**
 * Edit mode with a drawn puppet: pose, names, selection and twins, hovered node, isolate, view, px, the leaf
 * lengths from this frame's picks (a toe bone ends where its image ends) and the joints' pivot fractions
 * (`jointFracsOf`: the bones land on the drawn pivot pixels); else null.
 */
export function marksOf(ctx: ViewportCtx): MarksInput | null {
  const p = ctx.puppet();
  if (ctx.mode !== 'edit' || p === null || ctx.scene === null) return null;
  const s = ctx.store.getState();
  if (nothingToMark(s)) return null;
  const view = ctx.scene.camera.view();
  const px = cssToTarget(ctx) / view.pixelsPerUnit;
  const state = p.instance.state();
  const leaf = leafScratchOf(p);
  const profile = ctx.profile ?? p.bundle.profile;
  const ppu = profile.pixelsPerUnit;
  return {
    pose: state.pose, names: p.names, selected: s.selection.nodes, twins: twinsOf(s), hover: ctx.hoverNode,
    isolate: ctx.isolate || !s.camera.joints, view, px, leafLen: leafLengths(state.picks, leaf.nodeOf, ppu, p.names.length, leaf.buf),
    fracs: jointFracsOf(p, profile),
  };
}
